<template>
    <nav
        class="fixed top-0 w-full z-50 transition-transform duration-300 backdrop-blur-md bg-black bg-opacity-30 text-white shadow-lg"
    >
        <div
            class="max-w-7xl mx-auto flex justify-between items-center px-6 py-4"
        >
            <!-- Logo -->
            <div class="text-2xl font-bold tracking-wider drop-shadow-md">
                AGRIFUTURA
            </div>

            <!-- Desktop Nav -->
            <ul
                class="hidden md:flex space-x-10 font-semibold text-lg md:text-xl"
            >
                <li
                    v-for="link in navLinks"
                    :key="link.target"
                    class="relative group"
                >
                    <SmoothScrollLink
                        :target="link.target"
                        class="hover:text-green-400 hover:scale-105 transition-all drop-shadow-md"
                    >
                        {{ link.label }}
                    </SmoothScrollLink>
                    <span
                        class="absolute left-0 -bottom-1 w-0 h-0.5 bg-green-500 transition-all group-hover:w-full"
                    ></span>
                </li>
            </ul>

            <!-- Mobile Hamburger -->
            <button class="md:hidden text-4xl" @click="menuOpen = true">
                ☰
            </button>
        </div>

        <!-- Mobile Menu -->
        <transition name="fade">
            <div v-if="menuOpen" class="fixed inset-0 z-40">
                <!-- Backdrop -->
                <div
                    class="absolute inset-0 bg-black bg-opacity-95 backdrop-blur-md"
                    @click="menuOpen = false"
                ></div>

                <!-- Content -->
                <div
                    class="relative h-full flex flex-col items-center justify-center space-y-8 text-3xl font-semibold"
                >
                    <button
                        class="absolute top-6 right-6 text-4xl hover:text-green-400"
                        @click="menuOpen = false"
                    >
                        ✖
                    </button>

                    <ul class="space-y-6 text-center">
                        <li
                            v-for="(link, i) in navLinks"
                            :key="link.target"
                            class="opacity-0 animate-fade-in"
                            :style="{ animationDelay: `${i * 100}ms` }"
                        >
                            <SmoothScrollLink
                                :target="link.target"
                                @click.native="menuOpen = false"
                                class="hover:text-green-400 hover:scale-110 transition-all drop-shadow-lg"
                            >
                                {{ link.label }}
                            </SmoothScrollLink>
                        </li>
                    </ul>
                </div>
            </div>
        </transition>
    </nav>
</template>

<script setup>
import { ref, onMounted, onUnmounted, watch } from "vue";
import SmoothScrollLink from "./SmoothScrollLink.vue";

const menuOpen = ref(false);
const hiddenTop = ref(false);
const isMobile = ref(false);
let lastScrollY = 0;

const checkMobile = () => {
    isMobile.value = window.innerWidth < 768;
};

const handleScroll = () => {
    if (isMobile.value) {
        hiddenTop.value = false;
        return;
    }

    hiddenTop.value = window.scrollY > lastScrollY && window.scrollY > 100;
    lastScrollY = window.scrollY;
};

watch(menuOpen, (open) => {
    document.body.style.overflow = open ? "hidden" : "";
});

const navLinks = [
    { label: "Home", target: "#hero" },
    { label: "About", target: "#about" },
    { label: "Mission", target: "#mission" },
    { label: "Projects", target: "#projects" },
    { label: "Portfolios", target: "#portfolio" },
    { label: "Testimonials", target: "#testimonials" },
    { label: "Investment", target: "#investment" },
    { label: "Team", target: "#team" },
    { label: "Contact", target: "#contact" },
];

onMounted(() => {
    checkMobile();
    window.addEventListener("resize", checkMobile);
    window.addEventListener("scroll", handleScroll, { passive: true });
});

onUnmounted(() => {
    window.removeEventListener("resize", checkMobile);
    window.removeEventListener("scroll", handleScroll);
});
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.4s ease;
}
.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}

@keyframes fade-in {
    from {
        opacity: 0;
        transform: translateY(20px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}
.animate-fade-in {
    animation: fade-in 0.6s ease forwards;
}

.drop-shadow-md {
    text-shadow: 1px 1px 3px rgba(0, 0, 0, 0.8);
}
.drop-shadow-lg {
    text-shadow: 2px 2px 6px rgba(0, 0, 0, 0.9);
}
</style>
